import { useHistory } from 'react-router-dom';
import React from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { Grid, Hidden } from '@material-ui/core';
import { CompensationProps } from '../../../models/arc-epi-props';
import { FormWrapper } from '../../../../../global/components/form-wrapper';
import { HousingProvidedOptions } from '../../../../../global/constants/lists';
import { CalculatedTotalsDisplay } from '../shared/calculated-totals';
import { CalculatedTotals, ScheduledPayment } from '../compensation-history/compensation-history-model';
import { Employment } from '../../employment/employment-model';
import { getFormattedDate } from '../../../../../global/helpers/date-helpers';
import { ScheduledPaymentFormValues, ScheduledPaymentValidationSchema } from './scheduled-payment-form-model';
import { parseCurrency } from '../../../../../global/helpers/form-data-helper';
import { NumberField, NumberInputTypes } from '../../../../../global/components/form-element/number-field';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { FormGroup } from '../../../../../global/components/layout/form-group';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { FormField } from '../../../../../global/components/form-element/form-field';
import { useApiResponseError } from '../../../../../global/hooks/useApiError';
import { FormBaseModel } from '../../../../../global/models/page-model';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { ProcessName, RegisterStartProcess } from '../../../../../global/helpers/analytics-helper';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { getGpsType } from '../../../../../global/helpers/gps-helper';
import { FieldAutomationIds } from '../../../../../global/constants/field-automation-ids';

interface ScheduledPaymentFormProps extends FormBaseModel{
    onSubmitHandler: (savedValues: ScheduledPaymentFormValues, values: ScheduledPaymentFormValues) => any;
    compensationProps: CompensationProps;
    labelSettings: LabelSettingProps;
    currentScheduledPayment: ScheduledPayment;
    currentEmployment: Employment;
    onCalculateTotals: (values: ScheduledPaymentFormValues) => void;
    temporaryCalculatedTotals: CalculatedTotals;
    previousRoute: string;
    isEdit: boolean;
}
export function ScheduledPaymentForm(props: ScheduledPaymentFormProps) {
    const {
        onSubmitHandler,
        onCalculateTotals,
        compensationProps,
        labelSettings,
        currentScheduledPayment,
        currentEmployment,
        temporaryCalculatedTotals,
        previousRoute,
        isEdit } = props;

    const history = useHistory();
    const apiErrors = useApiResponseError();
    const gpsItem = getGpsType(null, null);

    RegisterStartProcess(ProcessName.ScheduledPayment);

    const initialValues: ScheduledPaymentFormValues = {
        effective_date: isEdit ? getFormattedDate(currentScheduledPayment?.effective_date) : null,
        cash_salary: parseCurrency(currentScheduledPayment?.cash_salary),
        er_plan_contributions: parseCurrency(currentScheduledPayment?.er_plan_contributions),
        housing_cash_allowance: parseCurrency(currentScheduledPayment?.housing_cash_allowance),
        housing_provided: currentScheduledPayment?.housing_provided,
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values) => onSubmitHandler(initialValues, values)}
            validationSchema={ScheduledPaymentValidationSchema(compensationProps, labelSettings)}
            enableReinitialize
            initialErrors={apiErrors}
        >
            {(formProps: FormikProps<ScheduledPaymentFormValues>) => (
                <Form>
                    <Grid>
                        <FormWrapper
                            formikProps={formProps}
                            submitText={labelSettings.submitText}
                            cancelText={labelSettings.cancelText}
                            onCancel={() => history.push(previousRoute)}
                        >
                            <PageHeader
                                headerText={props.formHeader}
                                description={props.formDescription}
                            />
                            <Hidden mdUp>
                                <SideBarContent>
                                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                                </SideBarContent>
                            </Hidden>
                            <DividerSection>
                                <ContentSpacer>
                                    <p className='input-label'>{compensationProps.hireDateLabel}</p>
                                    <p>{getFormattedDate(currentEmployment.hire_date)}</p>
                                </ContentSpacer>
                                <FormField
                                    name='effective_date'
                                    label={compensationProps.compEffectiveDateLabel}
                                    component='datePicker'
                                    touched={formProps.touched}
                                    errors={formProps.errors}
                                    required
                                    disabled={isEdit}
                                    autoWidth
                                    testId={FieldAutomationIds.scheduleHoursEffectiveDate}
                                />
                            </DividerSection>
                            <DividerSection sectionText={compensationProps.annualIncomeAmountsHeader}>
                                <FormGroup>
                                    <NumberField
                                        type={NumberInputTypes.Currency}
                                        name='cash_salary'
                                        label={compensationProps.baseSalaryScheduledTaxablePaymentsLabel}
                                        formikProps={formProps}
                                        onBlur={() => onCalculateTotals(formProps.values)}
                                        tooltip={compensationProps.baseSalaryLabelHelperText}
                                    />

                                    <NumberField
                                        type={NumberInputTypes.Currency}
                                        name='er_plan_contributions'
                                        label={compensationProps.employerContributionsLabel}
                                        formikProps={formProps}
                                        onBlur={() => onCalculateTotals(formProps.values)}
                                        tooltip={compensationProps.employerContributionLabelHelperText}
                                    />

                                    <NumberField
                                        type={NumberInputTypes.Currency}
                                        name='housing_cash_allowance'
                                        label={compensationProps.cashHousingAllowanceLabel}
                                        formikProps={formProps}
                                        onBlur={() => onCalculateTotals(formProps.values)}
                                        tooltip={compensationProps.cashHousingAllowanceLabelHelperText}
                                    />
                                </FormGroup>
                            </DividerSection>
                            <DividerSection sectionText={compensationProps.housingHeader}>
                              
                                <FormField
                                    name='housing_provided'
                                    label={compensationProps.providedHousingLabel}
                                    component='radioGroup'
                                    options={HousingProvidedOptions}
                                    touched={formProps.touched}
                                    errors={formProps.errors}
                                    onChange={(e) => onCalculateTotals({
                                        ...formProps.values,
                                        housing_provided: e.target.value,
                                    })}
                                    required
                                    tooltip={{ title: compensationProps.providedHousingLabelHelperText }}
                                   
                                />
                            </DividerSection>
                            <DividerSection removeDivider greySection sectionText={compensationProps.calculatedTotalsHeader}>
                                <CalculatedTotalsDisplay
                                    compensationProps={compensationProps}
                                    calculatedTotals={temporaryCalculatedTotals || currentScheduledPayment}
                                />
                            </DividerSection>
                        </FormWrapper>
                    </Grid>
                </Form>

            )}
        </Formik>
    );
}